import { Injectable } from '@angular/core';
import environment from '@environments/environment';
import { CodeFeaturesService } from '../user/code-features.service';

declare let gtag: Function;

@Injectable({
  providedIn: 'root'
})
export class GoogleTagManagerService {
  private isScriptLoaded = false;
  private isScriptLoading = false;

  constructor(private codeFeaturesService: CodeFeaturesService) {}

  /**
   * Tracks a page view if GTM is enabled and the page is not in the disabled list.
   * Dynamically loads the GTM script on first eligible page navigation.
   */
  trackPageView(url: string): void {
    if (!this.codeFeaturesService.isGoogleTagMgrEnabled || this.isPageDisabled(url)) {
      return;
    }

    this.loadGtmScript().then(() => {
      gtag('config', environment.googleAnalyticsCode, {
        'page_path': url
      });
    });
  }

  private isPageDisabled(url: string): boolean {
    const disabledList = environment.googleTagManagerDisabledPageList;
    if (!disabledList) {
      return false;
    }

    return disabledList
      .split(',')
      .map((path: string) => path.trim())
      .includes(url);
  }

  private loadGtmScript(): Promise<void> {
    if (this.isScriptLoaded) {
      return Promise.resolve();
    }

    if (this.isScriptLoading) {
      // Wait for the script to finish loading
      return new Promise((resolve) => {
        const checkLoaded = setInterval(() => {
          if (this.isScriptLoaded) {
            clearInterval(checkLoaded);
            resolve();
          }
        }, 50);
      });
    }

    this.isScriptLoading = true;

    return new Promise((resolve) => {
      // Initialize dataLayer and gtag function
      (window as any).dataLayer = (window as any).dataLayer || [];
      (window as any).gtag = function() {
        (window as any).dataLayer.push(arguments);
      };
      gtag('js', new Date());

      // Load the GTM script
      const script = document.createElement('script');
      script.async = true;
      script.src = 'https://www.googletagmanager.com/gtag/js';
      script.onload = () => {
        this.isScriptLoaded = true;
        this.isScriptLoading = false;
        resolve();
      };
      script.onerror = () => {
        this.isScriptLoading = false;
        // Resolve anyway to prevent blocking, GTM just won't work
        resolve();
      };
      document.head.appendChild(script);
    });
  }
}

